// pdks-tenant-frontend/src/components/RoleManagementPage.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    ShieldCheck,
    ShieldAlert,
    CheckCircle,
    PlusCircle,
    X,
    Loader2,
    AlertCircle,
    Search,
    Users,
    Key,
    Edit3,
    Trash2,
    CheckSquare,
    Square,
    RotateCcw,
    Lock,
    Filter
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

const RoleManagementPage = () => {
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [checkedPermissionIds, setCheckedPermissionIds] = useState(new Set());

    const [loading, setLoading] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);

    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Filtre ve Arama state'leri
    const [roleSearch, setRoleSearch] = useState('');
    const [permSearch, setPermSearch] = useState('');
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('ALL');
    const [showOnlyChecked, setShowOnlyChecked] = useState(false);

    // Modallar: Yeni Rol, Rol Düzenle, Rol Sil
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const [newRoleData, setNewRoleData] = useState({ name: '', description: '', requiresMfa: false });
    const [editRoleData, setEditRoleData] = useState({ name: '', description: '', requiresMfa: false });

    const { isAuthenticated, permissions = [], refreshSessionPermissions } = useAuth();

    // --- Yetki kontrolleri (roles:update_permissions ve geriye dönük roles:manage_permissions) ---
    const canView =
        permissions.includes('roles:view_list') ||
        permissions.includes('roles:update_permissions') ||
        permissions.includes('roles:manage_permissions') ||
        permissions.includes('roles:update');

    const canUpdateRoleInfo =
        permissions.includes('roles:update') ||
        permissions.includes('roles:update_permissions') ||
        permissions.includes('roles:manage_permissions');

    const canUpdatePermissions =
        permissions.includes('roles:update_permissions') ||
        permissions.includes('roles:manage_permissions') ||
        permissions.includes('roles:update');

    const canCreate =
        permissions.includes('roles:create') ||
        permissions.includes('roles:update_permissions') ||
        permissions.includes('roles:manage_permissions');

    const canDelete =
        permissions.includes('roles:delete') ||
        permissions.includes('roles:update_permissions') ||
        permissions.includes('roles:manage_permissions');

    // --- Roller + tüm izinler yükleme (selectedRole bağımlılığı kaldırıldı -> gereksiz API çağrısı önlendi) ---
    const fetchRolesAndPermissions = useCallback(async () => {
        if (!canView) {
            setError('Rolleri listeleme yetkiniz bulunmamaktadır.');
            setLoading(false);
            setInitialLoaded(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const [rolesRes, permissionsRes] = await Promise.all([
                apiClient('roles'),
                apiClient('roles/permissions'),
            ]);

            const rolesData = Array.isArray(rolesRes) ? rolesRes : [];
            const permsData = Array.isArray(permissionsRes) ? permissionsRes : [];

            setRoles(rolesData);
            setAllPermissions(permsData);

            setSelectedRole((prevSelected) => {
                if (prevSelected) {
                    const updatedMatch = rolesData.find((r) => r.id === prevSelected.id);
                    if (updatedMatch) {
                        return {
                            ...updatedMatch,
                            permissions: Array.isArray(updatedMatch.permissions) ? updatedMatch.permissions : [],
                        };
                    }
                }
                if (rolesData.length > 0) {
                    const first = rolesData[0];
                    return {
                        ...first,
                        permissions: Array.isArray(first.permissions) ? first.permissions : [],
                    };
                }
                return null;
            });
        } catch (err) {
            console.error('Roller veya izinler getirilirken hata oluştu:', err);
            setError(err.payload?.message || err.message || 'Roller veya izinler getirilirken bir hata oluştu.');
        } finally {
            setLoading(false);
            setInitialLoaded(true);
        }
    }, [canView]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchRolesAndPermissions();
        }
    }, [isAuthenticated, fetchRolesAndPermissions]);

    // --- Seçili rol değişince checkbox ve form state'ini güncelle ---
    useEffect(() => {
        if (selectedRole && Array.isArray(selectedRole.permissions)) {
            const ids = new Set(selectedRole.permissions.map((p) => p.id));
            setCheckedPermissionIds(ids);
            setEditRoleData({
                name: selectedRole.name || '',
                description: selectedRole.description || '',
                requiresMfa: !!selectedRole.requiresMfa,
            });
        } else {
            setCheckedPermissionIds(new Set());
            setEditRoleData({ name: '', description: '', requiresMfa: false });
        }
    }, [selectedRole]);

    // --- Değişiklik var mı (isDirty) kontrolü ---
    const isDirty = useMemo(() => {
        if (!selectedRole) return false;
        const origIds = new Set((selectedRole.permissions || []).map((p) => p.id));
        if (origIds.size !== checkedPermissionIds.size) return true;
        for (const id of checkedPermissionIds) {
            if (!origIds.has(id)) return true;
        }
        if ((selectedRole.requiresMfa || false) !== (editRoleData.requiresMfa || false)) return true;
        if ((selectedRole.name || '') !== (editRoleData.name || '')) return true;
        if ((selectedRole.description || '') !== (editRoleData.description || '')) return true;
        return false;
    }, [selectedRole, checkedPermissionIds, editRoleData]);

    // --- Tüm grup isimleri ---
    const allGroupNames = useMemo(() => {
        const set = new Set();
        allPermissions.forEach((p) => set.add(p.groupName || 'Diğer İzinler'));
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [allPermissions]);

    // --- İzinleri filtrele ve gruplara göre organize et ---
    const groupedPermissions = useMemo(() => {
        const q = permSearch.trim().toLocaleLowerCase('tr-TR');
        const groups = {};

        allPermissions.forEach((permission) => {
            const groupName = permission.groupName || 'Diğer İzinler';
            if (selectedGroupFilter !== 'ALL' && groupName !== selectedGroupFilter) return;
            if (showOnlyChecked && !checkedPermissionIds.has(permission.id)) return;

            if (q) {
                const matchName = (permission.displayName || '').toLocaleLowerCase('tr-TR').includes(q);
                const matchKey = (permission.key || '').toLocaleLowerCase('tr-TR').includes(q);
                const matchGroup = groupName.toLocaleLowerCase('tr-TR').includes(q);
                if (!matchName && !matchKey && !matchGroup) return;
            }

            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(permission);
        });
        return groups;
    }, [allPermissions, permSearch, selectedGroupFilter, showOnlyChecked, checkedPermissionIds]);

    // --- Sol listedeki rolleri filtrele ---
    const filteredRoles = useMemo(() => {
        const q = roleSearch.trim().toLocaleLowerCase('tr-TR');
        if (!q) return roles;
        return roles.filter(
            (r) =>
                (r.name || '').toLocaleLowerCase('tr-TR').includes(q) ||
                (r.description || '').toLocaleLowerCase('tr-TR').includes(q)
        );
    }, [roles, roleSearch]);

    // --- Tekil Checkbox toggle ---
    const handlePermissionToggle = (permissionId) => {
        if (!canUpdatePermissions || !selectedRole) return;
        setCheckedPermissionIds((prev) => {
            const next = new Set(prev);
            if (next.has(permissionId)) {
                next.delete(permissionId);
            } else {
                next.add(permissionId);
            }
            return next;
        });
    };

    // --- Grup bazlı Tümünü Seç / Kaldır ---
    const handleToggleGroup = (permsInGroup) => {
        if (!canUpdatePermissions || !selectedRole) return;
        const allSelectedInGroup = permsInGroup.every((p) => checkedPermissionIds.has(p.id));
        setCheckedPermissionIds((prev) => {
            const next = new Set(prev);
            permsInGroup.forEach((p) => {
                if (allSelectedInGroup) {
                    next.delete(p.id);
                } else {
                    next.add(p.id);
                }
            });
            return next;
        });
    };

    // --- Tüm İzinleri Seç / Temizle ---
    const handleSelectAllPermissions = () => {
        if (!canUpdatePermissions || !selectedRole) return;
        setCheckedPermissionIds(new Set(allPermissions.map((p) => p.id)));
    };

    const handleClearAllPermissions = () => {
        if (!canUpdatePermissions || !selectedRole) return;
        setCheckedPermissionIds(new Set());
    };

    const handleResetChanges = () => {
        if (!selectedRole) return;
        setCheckedPermissionIds(new Set((selectedRole.permissions || []).map((p) => p.id)));
        setEditRoleData({
            name: selectedRole.name || '',
            description: selectedRole.description || '',
            requiresMfa: !!selectedRole.requiresMfa,
        });
        setError(null);
    };

    // --- İzinleri ve Rol Özelliklerini Kaydet ---
    const handleSaveChanges = async () => {
        if (!selectedRole) return;

        if (!canUpdatePermissions && !canUpdateRoleInfo) {
            setError('Bu rolü güncelleme yetkiniz bulunmamaktadır.');
            return;
        }

        setIsSaving(true);
        setError(null);
        setMessage(null);
        setSaveSuccess(false);

        try {
            if (canUpdateRoleInfo) {
                await apiClient(`roles/${selectedRole.id}`, {
                    method: 'PUT',
                    body: {
                        name: editRoleData.name.trim() || selectedRole.name,
                        description: editRoleData.description?.trim() || '',
                        requiresMfa: editRoleData.requiresMfa,
                    },
                });
            }

            if (canUpdatePermissions) {
                await apiClient(`roles/${selectedRole.id}/permissions`, {
                    method: 'PUT',
                    body: {
                        permissionIds: Array.from(checkedPermissionIds),
                    },
                });
            }

            const updatedPermObjects = allPermissions.filter((p) => checkedPermissionIds.has(p.id));
            const updatedRoleObj = {
                ...selectedRole,
                name: editRoleData.name.trim() || selectedRole.name,
                description: editRoleData.description?.trim() || '',
                requiresMfa: editRoleData.requiresMfa,
                permissions: updatedPermObjects,
            };

            setSelectedRole(updatedRoleObj);
            setRoles((prevRoles) =>
                prevRoles.map((r) => (r.id === selectedRole.id ? updatedRoleObj : r))
            );

            setSaveSuccess(true);
            setMessage(`"${updatedRoleObj.name}" rolünün özellikleri ve yetkileri başarıyla kaydedildi.`);

            // Eğer kullanıcı kendi rolünün yetkilerini değiştirdiyse sol menüyü anında güncelle
            await refreshSessionPermissions?.();

            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error('Rol kaydedilirken hata:', err);
            setError(err.payload?.message || err.message || 'Rol kaydedilirken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    // --- Rol Adı & Açıklaması Düzenleme Modalı Kaydet ---
    const handleSaveRoleMetadataModal = async (e) => {
        e.preventDefault();
        if (!selectedRole || !canUpdateRoleInfo) return;
        if (!editRoleData.name.trim()) {
            setError('Rol adı boş bırakılamaz.');
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            await apiClient(`roles/${selectedRole.id}`, {
                method: 'PUT',
                body: {
                    name: editRoleData.name.trim(),
                    description: editRoleData.description?.trim() || '',
                    requiresMfa: editRoleData.requiresMfa,
                },
            });

            const updatedRoleObj = {
                ...selectedRole,
                name: editRoleData.name.trim(),
                description: editRoleData.description?.trim() || '',
                requiresMfa: editRoleData.requiresMfa,
            };
            setSelectedRole(updatedRoleObj);
            setRoles((prev) => prev.map((r) => (r.id === selectedRole.id ? updatedRoleObj : r)));
            setIsEditModalOpen(false);
            setMessage(`"${updatedRoleObj.name}" rol bilgileri güncellendi.`);
        } catch (err) {
            setError(err.payload?.message || err.message || 'Rol bilgileri güncellenirken hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    // --- Rol Silme ---
    const handleDeleteRole = async () => {
        if (!selectedRole || !canDelete) return;
        setIsSaving(true);
        setError(null);
        try {
            await apiClient(`roles/${selectedRole.id}`, {
                method: 'DELETE',
            });

            const deletedName = selectedRole.name;
            const remaining = roles.filter((r) => r.id !== selectedRole.id);
            setRoles(remaining);
            setSelectedRole(remaining.length > 0 ? remaining[0] : null);
            setIsDeleteModalOpen(false);
            setMessage(`"${deletedName}" rolü başarıyla silindi.`);
        } catch (err) {
            setError(err.payload?.message || err.message || 'Rol silinirken bir hata oluştu.');
            setIsDeleteModalOpen(false);
        } finally {
            setIsSaving(false);
        }
    };

    // --- Yeni rol oluşturma ---
    const handleAddNewRole = async (e) => {
        e.preventDefault();

        if (!canCreate) {
            setError('Yeni rol oluşturma yetkiniz bulunmamaktadır.');
            return;
        }

        if (!newRoleData.name.trim()) {
            setError('Rol adı zorunludur.');
            return;
        }

        setIsSaving(true);
        setError(null);
        setMessage(null);

        try {
            const createdRole = await apiClient('roles', {
                method: 'POST',
                body: {
                    name: newRoleData.name.trim(),
                    description: newRoleData.description?.trim() || '',
                    requiresMfa: newRoleData.requiresMfa,
                },
            });

            const roleWithPermissions = {
                ...createdRole,
                userCount: 0,
                permissions: Array.isArray(createdRole.permissions) ? createdRole.permissions : [],
            };

            setRoles((prev) => [...prev, roleWithPermissions]);
            setSelectedRole(roleWithPermissions);
            setNewRoleData({ name: '', description: '', requiresMfa: false });
            setIsAddModalOpen(false);
            setMessage(`"${roleWithPermissions.name}" rolü başarıyla oluşturuldu. Şimdi sağ panelden izinlerini seçip kaydedebilirsiniz.`);
        } catch (err) {
            console.error('Yeni rol oluşturulurken hata:', err);
            setError(err.payload?.message || err.message || 'Yeni rol oluşturulurken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRoleClick = (role) => {
        setSelectedRole({
            ...role,
            permissions: Array.isArray(role.permissions) ? role.permissions : [],
        });
        setMessage(null);
        setError(null);
    };

    const isSystemAdminRole =
        selectedRole && (selectedRole.name?.toLowerCase() === 'admin' || selectedRole.id === 1);

    if (!canView) {
        return (
            <div className="p-4 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 rounded-xl flex items-center border border-yellow-300 dark:border-yellow-700">
                <ShieldAlert className="w-5 h-5 mr-2 shrink-0" />
                Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.
            </div>
        );
    }

    if (!initialLoaded && loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col p-1 space-y-3">
            {/* Bildirim Bannerları */}
            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-700/70 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <span className="text-sm font-medium">{message}</span>
                    </div>
                    <button onClick={() => setMessage(null)} className="text-emerald-600 dark:text-emerald-400 hover:opacity-75">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {error && (
                <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700/70 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2.5 text-red-600 dark:text-red-400 shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:opacity-75">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Ana Grid */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[calc(100vh-190px)]">
                {/* SOL KOLON: Roller Listesi (4 Kolon) */}
                <div className="lg:col-span-4 bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200/80 dark:border-gray-700/80 p-4 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                    Roller ({roles.length})
                                </h2>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                    Firma içi yetki grupları
                                </p>
                            </div>
                        </div>
                        {canCreate && (
                            <button
                                type="button"
                                onClick={() => {
                                    setNewRoleData({ name: '', description: '', requiresMfa: false });
                                    setIsAddModalOpen(true);
                                    setError(null);
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-colors"
                            >
                                <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
                                Yeni Rol
                            </button>
                        )}
                    </div>

                    {/* Rol Arama */}
                    <div className="relative mb-3">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            type="text"
                            value={roleSearch}
                            onChange={(e) => setRoleSearch(e.target.value)}
                            placeholder="Rol adı veya açıklama ara..."
                            className="w-full pl-9 pr-8 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {roleSearch && (
                            <button
                                type="button"
                                onClick={() => setRoleSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700/70 pt-2 flex-1 overflow-y-auto space-y-1.5 pr-1">
                        {filteredRoles.length === 0 ? (
                            <p className="text-xs text-center py-8 text-gray-500 dark:text-gray-400">
                                {roleSearch ? 'Aramanızla eşleşen rol bulunamadı.' : 'Henüz tanımlı rol bulunmamaktadır.'}
                            </p>
                        ) : (
                            filteredRoles.map((role) => {
                                const isSelected = selectedRole?.id === role.id;
                                const permCount = Array.isArray(role.permissions) ? role.permissions.length : 0;
                                const userCount = role.userCount ?? 0;
                                const isAdmin = role.name?.toLowerCase() === 'admin' || role.id === 1;

                                return (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => handleRoleClick(role)}
                                        className={`w-full text-left p-3 rounded-xl border transition-all ${
                                            isSelected
                                                ? 'bg-indigo-50/90 dark:bg-indigo-900/40 border-indigo-500 dark:border-indigo-400 shadow-sm'
                                                : 'bg-gray-50/60 dark:bg-gray-900/30 border-gray-200/80 dark:border-gray-700/70 hover:bg-gray-100 dark:hover:bg-gray-800/80'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <span
                                                    className={`text-sm font-bold truncate ${
                                                        isSelected
                                                            ? 'text-indigo-700 dark:text-indigo-200'
                                                            : 'text-gray-900 dark:text-gray-100'
                                                    }`}
                                                >
                                                    {role.name}
                                                </span>
                                                {isAdmin && (
                                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 shrink-0">
                                                        Sistem
                                                    </span>
                                                )}
                                                {role.requiresMfa && (
                                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 shrink-0">
                                                        MFA
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {role.description && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                                                {role.description}
                                            </p>
                                        )}

                                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200/60 dark:border-gray-700/50 text-[11px] text-gray-500 dark:text-gray-400">
                                            <span className="inline-flex items-center gap-1">
                                                <Key className="w-3 h-3 text-indigo-500" />
                                                <strong className="text-gray-700 dark:text-gray-200">{permCount}</strong> yetki
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <Users className="w-3 h-3 text-emerald-500" />
                                                <strong className="text-gray-700 dark:text-gray-200">{userCount}</strong> kullanıcı
                                            </span>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* SAĞ KOLON: Seçili Rolün İzinleri ve Ayarları (8 Kolon) */}
                <div className="lg:col-span-8 bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200/80 dark:border-gray-700/80 p-4 flex flex-col h-full">
                    {selectedRole ? (
                        <>
                            {/* Üst Başlık: Rol Bilgisi + Aksiyon Butonları */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                            {selectedRole.name}
                                        </h2>
                                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                                            {checkedPermissionIds.size} / {allPermissions.length} İzin Seçili
                                        </span>
                                        {isDirty && (
                                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 animate-pulse">
                                                Kaydedilmemiş Değişiklik Var
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {selectedRole.description || 'Bu rol için henüz bir açıklama girilmemiş.'}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {canUpdateRoleInfo && (
                                        <button
                                            type="button"
                                            onClick={() => setIsEditModalOpen(true)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                            title="Rol adını ve açıklamasını düzenle"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" />
                                            Bilgileri Düzenle
                                        </button>
                                    )}
                                    {canDelete && !isSystemAdminRole && (
                                        <button
                                            type="button"
                                            onClick={() => setIsDeleteModalOpen(true)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-red-200 dark:border-red-800/60 text-red-600 dark:text-red-400 bg-red-50/60 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                                            title="Bu rolü sil"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Rolü Sil
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* MFA Zorunluluğu + Toplu Seçim Araç Çubuğu */}
                            <div className="py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <label
                                        htmlFor="editRequiresMfa"
                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-900/25 border border-purple-200/80 dark:border-purple-800/60 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            id="editRequiresMfa"
                                            checked={editRoleData.requiresMfa}
                                            onChange={(e) =>
                                                setEditRoleData((prev) => ({
                                                    ...prev,
                                                    requiresMfa: e.target.checked,
                                                }))
                                            }
                                            disabled={!canUpdateRoleInfo || isSaving}
                                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                                        />
                                        <Lock className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                                        <span className="text-xs font-semibold text-purple-800 dark:text-purple-300">
                                            Bu roldeki kullanıcılar için İki Adımlı Doğrulama (MFA) zorunlu olsun
                                        </span>
                                    </label>

                                    {canUpdatePermissions && (
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={handleSelectAllPermissions}
                                                disabled={isSaving}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                            >
                                                <CheckSquare className="w-3.5 h-3.5" />
                                                Tümünü Seç
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleClearAllPermissions}
                                                disabled={isSaving}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                            >
                                                <Square className="w-3.5 h-3.5" />
                                                Tümünü Kaldır
                                            </button>
                                            {isDirty && (
                                                <button
                                                    type="button"
                                                    onClick={handleResetChanges}
                                                    disabled={isSaving}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 transition-colors"
                                                    title="Değişiklikleri geri al"
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" />
                                                    Sıfırla
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* İzin Arama ve Grup Filtresi */}
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                                    <div className="md:col-span-6 relative">
                                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={permSearch}
                                            onChange={(e) => setPermSearch(e.target.value)}
                                            placeholder="İzin adı, grup veya anahtar (key) ara..."
                                            className="w-full pl-9 pr-8 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        {permSearch && (
                                            <button
                                                type="button"
                                                onClick={() => setPermSearch('')}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    <div className="md:col-span-4 relative">
                                        <Filter className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        <select
                                            value={selectedGroupFilter}
                                            onChange={(e) => setSelectedGroupFilter(e.target.value)}
                                            className="w-full pl-8 pr-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                            <option value="ALL">Tüm Modül Grupları ({allGroupNames.length})</option>
                                            {allGroupNames.map((g) => (
                                                <option key={g} value={g}>
                                                    {g}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="md:col-span-2 flex items-center">
                                        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={showOnlyChecked}
                                                onChange={(e) => setShowOnlyChecked(e.target.checked)}
                                                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span>Sadece Seçililer</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* İzin Grupları Listesi */}
                            <div className="flex-1 mt-3 min-h-[280px] overflow-y-auto space-y-3 pr-1">
                                {Object.keys(groupedPermissions).length > 0 ? (
                                    Object.entries(groupedPermissions).map(([groupName, perms]) => {
                                        const selectedInGroupCount = perms.filter((p) =>
                                            checkedPermissionIds.has(p.id)
                                        ).length;
                                        const allSelectedInGroup =
                                            perms.length > 0 && selectedInGroupCount === perms.length;

                                        return (
                                            <div
                                                key={groupName}
                                                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm"
                                            >
                                                <div className="px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide">
                                                            {groupName}
                                                        </span>
                                                        <span
                                                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                                                selectedInGroupCount > 0
                                                                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                                                                    : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                                            }`}
                                                        >
                                                            {selectedInGroupCount} / {perms.length}
                                                        </span>
                                                    </div>

                                                    {canUpdatePermissions && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleGroup(perms)}
                                                            disabled={isSaving}
                                                            className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                                                        >
                                                            {allSelectedInGroup ? 'Grubu Kaldır' : 'Grubun Tümünü Seç'}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {perms.map((permission) => {
                                                        const isChecked = checkedPermissionIds.has(permission.id);
                                                        return (
                                                            <label
                                                                key={permission.id}
                                                                className={`flex items-start gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition-all ${
                                                                    isChecked
                                                                        ? 'bg-indigo-50/60 dark:bg-indigo-900/25 border-indigo-200 dark:border-indigo-700/70 text-gray-900 dark:text-gray-100'
                                                                        : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 shrink-0"
                                                                    checked={isChecked}
                                                                    onChange={() => handlePermissionToggle(permission.id)}
                                                                    disabled={!canUpdatePermissions || isSaving}
                                                                />
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="font-semibold leading-snug break-words">
                                                                        {permission.displayName || permission.name}
                                                                    </div>
                                                                    <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 truncate mt-0.5">
                                                                        {permission.key}
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-48 text-sm text-gray-500 dark:text-gray-400">
                                        <Search className="w-6 h-6 mb-2 text-gray-400" />
                                        Arama veya filtre kriterlerine uygun izin bulunamadı.
                                    </div>
                                )}
                            </div>

                            {/* Alt Kaydet Barı */}
                            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {saveSuccess ? (
                                        <span className="inline-flex items-center text-emerald-600 dark:text-emerald-400 font-semibold">
                                            <CheckCircle className="w-4 h-4 mr-1.5" />
                                            Değişiklikler başarıyla kaydedildi
                                        </span>
                                    ) : isDirty ? (
                                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                                            Kaydedilmemiş değişiklikleriniz var.
                                        </span>
                                    ) : (
                                        <span>Tüm değişiklikler güncel.</span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveChanges}
                                        disabled={!selectedRole || (!canUpdatePermissions && !canUpdateRoleInfo) || isSaving}
                                        className={`inline-flex items-center px-5 py-2.5 text-xs font-bold rounded-xl shadow-sm transition-all ${
                                            selectedRole && (canUpdatePermissions || canUpdateRoleInfo) && !isSaving
                                                ? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500'
                                                : 'bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        Değişiklikleri Kaydet
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                            <ShieldAlert className="w-10 h-10 mb-2 text-gray-400" />
                            <p className="text-sm font-medium">
                                İzinleri görüntülemek ve düzenlemek için sol listeden bir rol seçiniz.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL 1: Yeni Rol Oluştur */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md relative border border-gray-200 dark:border-gray-700 p-6">
                        <button
                            type="button"
                            onClick={() => !isSaving && setIsAddModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Yeni Rol Oluştur
                        </h3>
                        <form onSubmit={handleAddNewRole} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                    Rol Adı <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newRoleData.name}
                                    onChange={(e) => setNewRoleData((prev) => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                    placeholder="Örn: İnsan Kaynakları Uzmanı"
                                    autoFocus
                                    disabled={isSaving}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                    Açıklama
                                </label>
                                <textarea
                                    value={newRoleData.description}
                                    onChange={(e) =>
                                        setNewRoleData((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    rows={3}
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                                    placeholder="Bu rolün sorumluluklarını kısaca açıklayın..."
                                    disabled={isSaving}
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={newRoleData.requiresMfa}
                                    onChange={(e) =>
                                        setNewRoleData((prev) => ({ ...prev, requiresMfa: e.target.checked }))
                                    }
                                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                                    disabled={isSaving}
                                />
                                <span className="text-xs font-semibold text-purple-700 dark:text-purple-400">
                                    MFA (İki Adımlı Doğrulama) Zorunlu Olsun
                                </span>
                            </label>
                            <div className="flex items-center justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAddModalOpen(false)}
                                    disabled={isSaving}
                                    className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    Vazgeç
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving || !newRoleData.name.trim()}
                                    className="px-4 py-2 text-xs font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
                                >
                                    {isSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                                    Oluştur
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL 2: Rol Bilgilerini Düzenle */}
            {isEditModalOpen && selectedRole && (
                <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md relative border border-gray-200 dark:border-gray-700 p-6">
                        <button
                            type="button"
                            onClick={() => !isSaving && setIsEditModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Rol Bilgilerini Düzenle
                        </h3>
                        <form onSubmit={handleSaveRoleMetadataModal} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                    Rol Adı <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={editRoleData.name}
                                    onChange={(e) =>
                                        setEditRoleData((prev) => ({ ...prev, name: e.target.value }))
                                    }
                                    disabled={isSaving || isSystemAdminRole}
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm disabled:opacity-60"
                                />
                                {isSystemAdminRole && (
                                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                                        Sistem varsayılan "Admin" rolünün adı değiştirilemez.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                    Açıklama
                                </label>
                                <textarea
                                    value={editRoleData.description}
                                    onChange={(e) =>
                                        setEditRoleData((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    rows={3}
                                    disabled={isSaving}
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                                />
                            </div>
                            <div className="flex items-center justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    disabled={isSaving}
                                    className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    Vazgeç
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving || !editRoleData.name.trim()}
                                    className="px-4 py-2 text-xs font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
                                >
                                    {isSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                                    Güncelle
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL 3: Rol Silme Onayı */}
            {isDeleteModalOpen && selectedRole && (
                <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md relative border border-gray-200 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                            Rolü Silmek İstediğinize Emin Misiniz?
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                            <strong>"{selectedRole.name}"</strong> rolünü silmek üzeresiniz.
                            {selectedRole.userCount > 0 ? (
                                <span className="block mt-2 p-2.5 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold border border-red-200 dark:border-red-800">
                                    Uyarı: Bu role atanmış {selectedRole.userCount} aktif kullanıcı bulunmaktadır. Önce kullanıcıların rolünü değiştirmelisiniz.
                                </span>
                            ) : (
                                ' Bu işlem geri alınamaz.'
                            )}
                        </p>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isSaving}
                                className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Vazgeç
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteRole}
                                disabled={isSaving || selectedRole.userCount > 0}
                                className="px-4 py-2 text-xs font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center"
                            >
                                {isSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                                Evet, Rolü Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleManagementPage;
